import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Breadcrumbs,
  Link,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
  getSuppressions,
  clearSuppressionState,
  bulkUpsertSuppressions,
  clearBulkSuppressionMessages,
} from "../../../../../store/suppression/suppressionSlice";
import { isMSPUser } from "../../../../../helpers/util_helper";
import store from "../../../../../store";
import { formatScheduleName } from "../../utils/scheduleNameFormatter";

const Suppressions = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { scheduleId } = useParams();

  const { 
    suppressions, 
    isFetchingSuppressions, 
    fetchSuppressionsError,
    isSavingSuppressions,
    saveSuppressionsError,
    saveSuppressionsSuccess,
  } = useSelector((state) => state.suppression);

  // Get selected schedule from sessionStorage
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  
  // Sorting state
  const [sortField, setSortField] = useState(null); // null, 'framework_name', 'control_code', 'control_title', 'scope', 'check_title', 'created_by', 'created_at'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  
  // Reset dialog state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirmDialogOpen, setResetConfirmDialogOpen] = useState(false);
  const [resetStatus, setResetStatus] = useState({
    step: 0, // 0: not started, 1: resetting, 2: refreshing, 3: done
    message: "",
    error: null,
  });

  // Pagination state
  const [page, setPage] = useState(1);
  const API_PAGE_SIZE = 100; // API fetches 100 records at a time
  const DEFAULT_PAGE_SIZE = 25;
  
  // Get page size from localStorage or use default
  const getStoredPageSize = () => {
    const stored = localStorage.getItem("suppression_suppressions_page_size");
    return stored ? parseInt(stored, 10) : DEFAULT_PAGE_SIZE;
  };
  
  const [pageSize, setPageSize] = useState(getStoredPageSize());
  
  // Store all suppressions data and track fetched API pages
  const [allSuppressions, setAllSuppressions] = useState(null);
  const [fetchedApiPages, setFetchedApiPages] = useState(new Set([1]));

  useEffect(() => {
    // Retrieve schedule data from sessionStorage
    const scheduleData = sessionStorage.getItem("selectedSchedule");
    if (scheduleData) {
      try {
        setSelectedSchedule(JSON.parse(scheduleData));
      } catch (error) {
        console.error("Error parsing selectedSchedule:", error);
      }
    }
  }, []);

  // Fetch suppressions on mount
  useEffect(() => {
    if (scheduleId) {
      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: 1, 
          page_size: API_PAGE_SIZE 
        } 
      }));
    }
  }, [dispatch, scheduleId]);
  
  // Check if we need to fetch more data when page changes
  useEffect(() => {
    if (!allSuppressions?.items || !scheduleId) return;
    
    // Calculate which API page we need based on current UI page and page size
    const recordsNeeded = page * pageSize;
    const apiPageNeeded = Math.ceil(recordsNeeded / API_PAGE_SIZE);
    
    // If we haven't fetched this API page yet, fetch it
    if (apiPageNeeded > 0 && !fetchedApiPages.has(apiPageNeeded)) {
      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: apiPageNeeded, 
          page_size: API_PAGE_SIZE 
        } 
      }));
      setFetchedApiPages((prev) => new Set([...prev, apiPageNeeded]));
    }
  }, [page, pageSize, allSuppressions, fetchedApiPages, dispatch, scheduleId]);
  
  // Update allSuppressions when suppressions data is received (merge with existing data)
  useEffect(() => {
    if (suppressions && !isFetchingSuppressions && suppressions.items) {
      setAllSuppressions((prev) => {
        if (!prev) {
          return suppressions;
        }
        // Merge items, avoiding duplicates
        const existingIds = new Set(prev.items.map((s) => s.suppression_id));
        const newItems = suppressions.items.filter(
          (s) => !existingIds.has(s.suppression_id)
        );
        return {
          ...suppressions,
          items: [...prev.items, ...newItems],
          total: Math.max(prev.total || 0, suppressions.total || 0),
        };
      });
    }
  }, [suppressions, isFetchingSuppressions]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      dispatch(clearSuppressionState());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = () => {
    history.push("/configuration/suppression");
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (path) => {
    history.push(path);
  };

  // Filter and paginate suppressions client-side
  const filteredSuppressions = useMemo(() => {
    if (!allSuppressions?.items) return { items: [], total: 0, page: 1, page_size: pageSize };
    
    let filtered = [...allSuppressions.items];

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((suppression) => {
        return (
          suppression.framework_name?.toLowerCase().includes(searchLower) ||
          suppression.control_code?.toLowerCase().includes(searchLower) ||
          suppression.control_title?.toLowerCase().includes(searchLower) ||
          suppression.check_title?.toLowerCase().includes(searchLower) ||
          suppression.created_by?.toLowerCase().includes(searchLower) ||
          suppression.scope?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by scope
    if (scopeFilter) {
      filtered = filtered.filter((suppression) => {
        return suppression.scope?.toUpperCase() === scopeFilter.toUpperCase();
      });
    }

    // Sort data
    if (sortField) {
      filtered.sort((a, b) => {
        let aValue = a[sortField];
        let bValue = b[sortField];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Convert to string for comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();

        // Handle date sorting for created_at
        if (sortField === 'created_at') {
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        }

        let comparison = 0;
        if (aValue < bValue) {
          comparison = -1;
        } else if (aValue > bValue) {
          comparison = 1;
        }

        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    // Calculate pagination
    const total = filtered.length;
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = filtered.slice(startIndex, endIndex);

    return {
      items: paginatedItems,
      total,
      page,
      page_size: pageSize,
    };
  }, [allSuppressions, searchTerm, scopeFilter, sortField, sortDirection, page, pageSize]);
  
  // Handle page size change
  const handlePageSizeChange = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    localStorage.setItem("suppression_suppressions_page_size", newPageSize.toString());
    setPage(1); // Reset to first page when page size changes
  };
  
  // Handle pagination
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
    setPage(1); // Reset to first page when sorting changes
  };

  // Handle reset all suppressions for this schedule
  const handleResetSuppressions = async () => {
    if (!scheduleId) {
      console.error("Cannot reset: Missing schedule");
      return;
    }

    // Close confirmation dialog and open progress dialog
    setResetConfirmDialogOpen(false);
    setResetDialogOpen(true);
    setResetStatus({
      step: 0,
      message: "",
      error: null,
    });

    try {
      // Helper function to wait for API call to complete
      const waitForSaveOperation = () => {
        return new Promise((resolve, reject) => {
          const checkInterval = setInterval(() => {
            const state = store.getState();
            const suppressionState = state.suppression;
            const currentState = {
              isSaving: suppressionState.isSavingSuppressions,
              success: suppressionState.saveSuppressionsSuccess,
              error: suppressionState.saveSuppressionsError,
            };

            if (!currentState.isSaving) {
              clearInterval(checkInterval);
              if (currentState.error) {
                reject(new Error(currentState.error));
              } else if (currentState.success) {
                resolve(currentState.success);
              } else {
                setTimeout(() => {
                  const finalState = store.getState().suppression;
                  if (finalState.saveSuppressionsError) {
                    reject(new Error(finalState.saveSuppressionsError));
                  } else if (finalState.saveSuppressionsSuccess) {
                    resolve(finalState.saveSuppressionsSuccess);
                  } else {
                    reject(new Error("Operation completed but no result"));
                  }
                }, 200);
              }
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            reject(new Error("Operation timed out"));
          }, 30000);
        });
      };

      // Step 1: Reset all suppressions
      setResetStatus((prev) => ({
        ...prev,
        step: 1,
        message: "Resetting all suppressions...",
      }));

      dispatch(clearBulkSuppressionMessages());

      // Call API with reset=true, empty payload, no framework_id
      dispatch(bulkUpsertSuppressions({
        schedule_id: scheduleId,
        payload: [],
        queryParams: { 
          reset: true,
        },
      }));

      await waitForSaveOperation();
      console.log("Reset completed successfully");

      setResetStatus((prev) => ({
        ...prev,
        message: "Successfully reset all suppressions",
      }));

      // Step 2: Refresh suppressions data
      setResetStatus((prev) => ({
        ...prev,
        step: 2,
        message: "Refreshing suppressions...",
      }));

      // Reset fetched pages to force fresh data
      setFetchedApiPages(new Set([1]));
      setAllSuppressions(null);

      dispatch(getSuppressions({ 
        schedule_id: scheduleId, 
        params: { 
          page: 1, 
          page_size: API_PAGE_SIZE 
        } 
      }));

      // Wait for suppressions data to be fetched
      await new Promise((resolve, reject) => {
        const checkInterval = setInterval(() => {
          const state = store.getState();
          const suppressionState = state.suppression;
          
          if (!suppressionState.isFetchingSuppressions) {
            clearInterval(checkInterval);
            if (suppressionState.fetchSuppressionsError) {
              reject(new Error(suppressionState.fetchSuppressionsError));
            } else if (suppressionState.suppressions && suppressionState.suppressions.items) {
              setTimeout(() => {
                resolve(suppressionState.suppressions);
              }, 300);
            } else {
              setTimeout(() => {
                const finalState = store.getState().suppression;
                if (finalState.suppressions && finalState.suppressions.items) {
                  setTimeout(() => resolve(finalState.suppressions), 300);
                } else {
                  reject(new Error("Suppressions data not received"));
                }
              }, 500);
            }
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          reject(new Error("Refresh timed out"));
        }, 30000);
      });

      // Step 3: Done
      setResetStatus((prev) => ({
        ...prev,
        step: 3,
        message: "All operations completed successfully!",
      }));

      // Close dialog after a short delay
      setTimeout(() => {
        setResetDialogOpen(false);
        setResetStatus({
          step: 0,
          message: "",
          error: null,
        });
      }, 1500);
    } catch (error) {
      console.error("Error resetting suppressions:", error);
      setResetStatus((prev) => ({
        ...prev,
        error: getErrorMessage(error),
      }));
    }
  };

  // Helper function to safely extract error message
  const getErrorMessage = (error) => {
    if (!error) return "An unexpected error occurred";
    if (typeof error === "string") return error;
    if (typeof error === "object") {
      if (error.error && typeof error.error === "string") return error.error;
      if (error.message && typeof error.message === "string")
        return error.message;
      if (error.error && typeof error.error === "object") {
        if (error.error.message) return error.error.message;
        if (error.error.error) return error.error.error;
      }
      try {
        const stringified = JSON.stringify(error);
        if (stringified.length > 200) {
          return "An error occurred. Please check the console for details.";
        }
        return stringified;
      } catch {
        return "An unexpected error occurred";
      }
    }
    return String(error);
  };

  // Get pagination info
  const totalPages = filteredSuppressions?.total
    ? Math.ceil(filteredSuppressions.total / pageSize)
    : 0;
  const currentPageStart = filteredSuppressions?.items?.length
    ? (page - 1) * pageSize + 1
    : 0;
  const currentPageEnd = filteredSuppressions?.items?.length
    ? Math.min(page * pageSize, filteredSuppressions.total || 0)
    : 0;

  return (
    <Box
      sx={{
        padding: "1.5rem 2rem",
        mb: "3rem",
        backgroundColor: "#F9F9F9",
        minHeight: "100vh",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      }}
    >
      {/* Breadcrumbs */}
      <Box sx={{ mb: 2.5 }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => handleBreadcrumbClick("/configuration/suppression")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Schedules
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#1e293b",
              fontSize: "0.875rem",
              fontWeight: 500,
              px: 0.5,
            }}
          >
            Suppressions
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Header with Back Button on Right */}
      <Box sx={{ 
        mb: 2.5, 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "space-between",
        borderBottom: "1px solid #e5e7eb",
        pb: 1.5
      }}>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#374151",
              fontSize: "1rem",
              letterSpacing: "-0.01em",
              mb: 0.25,
            }}
          >
            Suppressions
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#6b7280",
              fontSize: "0.8125rem",
              fontWeight: 400,
            }}
          >
            {selectedSchedule
              ? `Schedule: ${formatScheduleName(selectedSchedule)} | Account: ${selectedSchedule.account_number || selectedSchedule.account_id || "N/A"}`
              : "Viewing suppressions for selected schedule"}
          </Typography>
        </Box>
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            color: "#64748b",
            backgroundColor: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 500,
            px: 2,
            py: 1,
            "&:hover": {
              backgroundColor: "#f8fafc",
              color: "#374151",
              borderColor: "#d1d5db",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Back
        </Button>
      </Box>

      {/* Loading State */}
      {isFetchingSuppressions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
          <Typography
            variant="body2"
            sx={{
              ml: 2,
              color: "#64748b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            Loading suppressions...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {fetchSuppressionsError && !isFetchingSuppressions && (
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #f87171",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#991b1b",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {getErrorMessage(fetchSuppressionsError)}
          </Typography>
        </Box>
      )}

      {/* Suppressions Table */}
      {allSuppressions && !isFetchingSuppressions && !fetchSuppressionsError && (
        <Card
          sx={{
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "0.875rem",
                }}
              >
                Suppressions
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                <TextField
                size="small"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: "#64748b" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 400,
                  "& .MuiOutlinedInput-root": {
                    fontSize: "0.875rem",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: "#d1d5db",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#3b82f6",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputBase-input": {
                    padding: "8px 12px",
                  },
                }}
              />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={scopeFilter}
                    onChange={(e) => {
                      setScopeFilter(e.target.value);
                      setPage(1); // Reset to first page when filter changes
                    }}
                    displayEmpty
                    sx={{
                      fontSize: "0.875rem",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      "& .MuiSelect-select": {
                        py: 0.75,
                        px: 1.5,
                      },
                    }}
                  >
                    <MenuItem value="">All Scopes</MenuItem>
                    <MenuItem value="CONTROL">Control</MenuItem>
                    <MenuItem value="CHECK">Check</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setResetConfirmDialogOpen(true)}
                  disabled={isSavingSuppressions || (filteredSuppressions?.total || 0) === 0}
                    sx={{
                      color: "#374151",
                      borderColor: "#e2e8f0",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      px: 2,
                      py: 0.75,
                      borderRadius: "6px",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        borderColor: "#d1d5db",
                        color: "#1e293b",
                      },
                      "&:disabled": {
                        backgroundColor: "transparent",
                        borderColor: "#e5e7eb",
                        color: "#9ca3af",
                      },
                    }}
                  >
                    Reset
                  </Button>
              </Box>
            </Box>

            <TableContainer
              sx={{
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f5f9",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#cbd5e1",
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#94a3b8",
                  },
                },
              }}
            >
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell
                      onClick={() => handleSort('framework_name')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Framework Name
                        {sortField === 'framework_name' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('control_code')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Control Id
                        {sortField === 'control_code' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('control_title')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Control Title
                        {sortField === 'control_title' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('scope')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Scope
                        {sortField === 'scope' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('check_title')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Check Title
                        {sortField === 'check_title' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('created_by')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      Updated By
                        {sortField === 'created_by' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      onClick={() => handleSort('created_at')}
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "0.75rem",
                        py: 1,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        Updated On
                        {sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                          )
                        ) : (
                          <UnfoldMoreIcon sx={{ fontSize: 14, color: "#cbd5e1" }} />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSuppressions?.items && filteredSuppressions.items.length > 0 ? (
                    filteredSuppressions.items.map((suppression, index) => (
                      <TableRow
                        key={suppression.suppression_id || index}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#f8fafc",
                            "& td": {
                              backgroundColor: "#f8fafc",
                            },
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.framework_name || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.control_code || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.control_title || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                            }}
                          >
                            {suppression.scope || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.check_title || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.created_by || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#374151",
                              fontSize: "0.75rem",
                            }}
                          >
                            {suppression.created_at
                              ? new Date(suppression.created_at).toLocaleString()
                              : "N/A"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 4, textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontSize: "0.875rem",
                          }}
                        >
                          No suppressions found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                px: 2,
                py: 1.5,
                borderTop: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                minHeight: 40,
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#6b7280", fontSize: "0.75rem" }}
                >
                  Rows per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    sx={{
                      fontSize: "0.75rem",
                      height: "28px",
                      "& .MuiSelect-select": {
                        py: 0.5,
                        px: 1,
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#374151", fontSize: "0.75rem", fontWeight: 500 }}
                >
                  Page {page} of {totalPages || 1}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <FirstPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  sx={{
                    padding: "4px",
                    color: page === 1 ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor: page === 1 ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <ArrowForwardIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                  sx={{
                    padding: "4px",
                    color: page >= totalPages ? "#d1d5db" : "#64748b",
                    "&:hover": {
                      backgroundColor:
                        page >= totalPages ? "transparent" : "#f1f5f9",
                    },
                  }}
                >
                  <LastPageIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Reset Confirmation Dialog */}
      <Dialog
        open={resetConfirmDialogOpen}
        onClose={() => setResetConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            color: "#1e293b",
            fontSize: "1.125rem",
            pb: 1,
          }}
        >
          Confirm Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#64748b", fontSize: "0.875rem" }}>
            Are you sure you want to reset all the suppressions?
          </DialogContentText>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
          }}
        >
          <Button
            onClick={() => setResetConfirmDialogOpen(false)}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#f1f5f9",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetSuppressions}
            variant="contained"
            sx={{
              backgroundColor: "#ef4444",
              color: "white",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#dc2626",
              },
            }}
          >
            Reset
          </Button>
        </Box>
      </Dialog>

      {/* Reset Status Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => {
          if (resetStatus.step === 3 || resetStatus.error) {
            setResetDialogOpen(false);
            setResetStatus({
              step: 0,
              message: "",
              error: null,
            });
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#f8fafc",
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{
                fontWeight: 600,
                color: "#1e293b",
                fontSize: "1.125rem",
                mb: 0.5,
              }}
            >
              {resetStatus.error ? "Error Resetting Suppressions" : "Resetting Suppressions"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#64748b",
                fontSize: "0.875rem",
                fontWeight: 400,
              }}
            >
              {resetStatus.error ? "An error occurred while resetting" : "Processing reset for this schedule"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              if (resetStatus.step === 3 || resetStatus.error) {
                setResetDialogOpen(false);
                setResetStatus({
                  step: 0,
                  message: "",
                  error: null,
                });
              }
            }}
            disabled={resetStatus.step > 0 && resetStatus.step < 3 && !resetStatus.error}
            sx={{
              color: "#64748b",
              "&:hover": {
                backgroundColor: "#f1f5f9",
                color: "#374151",
              },
              "&:disabled": {
                opacity: 0.5,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <DialogContent sx={{ padding: "24px" }}>
          {resetStatus.error ? (
            <DialogContentText sx={{ color: "error.main", mb: 2 }}>
              {resetStatus.error}
            </DialogContentText>
          ) : (
            <>
              {resetStatus.step > 0 && resetStatus.message && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#1e293b" }}>
                    {resetStatus.step === 1 ? "Resetting Suppressions" : resetStatus.step === 2 ? "Refreshing Data" : "Completed"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b", mb: 1.5 }}>
                    {resetStatus.message}
                  </Typography>
                  {resetStatus.step === 1 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                  {resetStatus.step === 2 && <LinearProgress sx={{ mt: 1, borderRadius: 1 }} />}
                </Box>
              )}
              {resetStatus.step === 3 && (
                <Box sx={{ textAlign: "center", py: 2 }}>
                  <Typography variant="body1" sx={{ color: "#10b981", fontWeight: 500 }}>
                    All operations completed successfully!
                  </Typography>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Suppressions;

